import {gsap} from "gsap/all";
import $ from "jquery";

export class SolarSystem {

  constructor(app, options = {}) {
    this.app = app;

    // The box the HTML planets and targets sit inside. SVG coordinates get mapped
    // into this element's pixels (see svgToLocal).
    this.$host = options.host ? $(options.host) : $(".solar-system");

    // How far along the arc the ship starts. 0 launches it from the arc's start,
    // which is the Earth target's center where it sat while being aimed, so it
    // pulls away from there rather than jumping out of the target first.
    this.shipStartOffset = 0;

    // Fallback for how long the ship takes to scale up at launch (seconds), when
    // the caller doesn't pass landing.scaleUpSec. See flyShipAlongArc.
    this.shipScaleUpSec = 2;

    // Added to the arc's direction so the ship's nose points the right way. The
    // ship art is drawn pointing up, and +90 turns that into the travel direction.
    // Use -90 instead if it ever points backwards.
    this.shipRotationOffset = 90;

    // The aim path is worked out on the fly from the drag: it leaves Earth on the
    // heading the player picked and curves round from there. It's a circle, so the
    // two things that describe it are how long it runs and how far round it goes.
    //   length - how long the path is, as a multiple of the Earth-start to
    //     Mars-arrival distance. It never changes with the force, and it's well
    //     past the edge of the system, so the path always runs off rather than
    //     stopping somewhere the player could read as a destination.
    //   sweepMax - how far round the path curves at no force, in degrees. Force
    //     straightens it from there, reaching a dead straight line at full force.
    //     Kept under a full turn, which is all one SVG arc can draw.
    //     Only the paths that curve a long way round get over the top of the sun
    //     and back down onto Mars, so this has to reach well past a half turn.
    //     What it really sets is which force wins: the arc that arrives on time
    //     without cutting inside Earth's orbit is a fixed shape (a half circle,
    //     see Game.shipSpeed), and this is the dial that decides where in the drag
    //     it falls. With the length above it lands at about a quarter force, which
    //     leaves the player room to miss either way. Drop it back toward 260 and
    //     the only launches that still reach Mars are the ones with almost no
    //     force at all.
    this.aimArc = options.aimArc ?? {
      length: 2.265,
      sweepMax: 340,
    };

    // Below this many degrees of sweep the path is drawn as a straight line: the
    // circle behind it would be so big that the numbers stop being worth trusting.
    this.aimStraightDeg = 0.5;

    // How far along the aim path the fade to nothing starts (0 to 1 of the straight
    // line between its ends). Everything before this is at full strength.
    this.aimFadeStart = options.aimFadeStart ?? 0.75;

    // How far the launch direction swings for a sideways drag, in degrees at each
    // end of the drag, measured from the heading at rest (the way Earth is already
    // travelling, which is up from its start spot).
    // At 180 the drag covers a whole turn, so every direction can be aimed, at rest
    // the ship points the way Earth is already travelling (up from its start spot),
    // and the two ends of the drag meet on the same downward heading. That last bit
    // means the extremes duplicate each other, which is fine: what the player has to
    // read off the aim isn't which end of the drag they're at, it's whether the ship
    // leaves upwards or downwards, and the path says so directly (see setAimUpstream).
    this.aimSweepDeg = options.aimSweepDeg ?? 180;

    // The aim path's current geometry ({start, end, bow} in SVG units), kept so the
    // release can snap it onto Mars and so the hit test knows where it ends.
    this.aimGeo = null;

    // How close (host pixels) the path's end has to get to Mars's arrival point for
    // the launch to count. Game overrides this from the capture indicator's own
    // size, so what you aim at is the disc you later have to tap.
    this.destinationRadiusPx = options.destinationRadiusPx ?? 78;

    // How far inside Earth's orbit (host pixels) a path is allowed to stray before
    // it counts as cutting across the inside of it. The ship leaves from a point on
    // that orbit, so a launch aimed even slightly sunward dips under it at once;
    // this is the slack that keeps a hair's width from failing a launch nobody
    // could see was wrong. Raise it and sunward aims start being let through.
    this.innerBoundSlackPx = options.innerBoundSlackPx ?? 6;

    // Two cues that are worked out either way but not shown, because each hands the
    // player the answer: the circle the launch has to land in, and colouring the arc
    // when it's aimed against Earth's motion. Off is the real game (see AimDebug).
    this.showTargetRing = options.showTargetRing ?? false;
    this.markUpstream = options.markUpstream ?? false;

    // The game reveals the target circle anyway, as a hint to a player who has been
    // dragging for a while (see Game.scheduleTargetRingHint). Kept apart from
    // showTargetRing so the debug switch stays on whatever the game does.
    this.targetRingRevealed = false;

    // The outline of every launch that would meet Mars, as an SVG path, shown in
    // green along with the target circle. Game works it out once (setGoodZone) and
    // it's kept here so every rebuild of the trajectory layer can draw it again.
    this.goodZoneD = null;

    // How strong the zone is at Earth, as the opacity of its green (0 to 1). It fades
    // out along the way, from there to nothing goodZoneFadeAt along the paths (0 to 1
    // of the distance flown to Mars). The line the fade gradient runs along is worked
    // out with the zone and kept with it.
    this.goodZoneStrength = options.goodZoneStrength ?? 0.4;
    this.goodZoneFadeAt = options.goodZoneFadeAt ?? 1;
    this.goodZoneFade = null;

    // How much the zone's edges are smoothed, as the share of the way along each
    // side that goes into the average (see setGoodZone). Raise it for rounder edges,
    // at the cost of the zone hugging the paths less closely.
    this.goodZoneSmoothing = options.goodZoneSmoothing ?? 0.275;

    // How far along the path the ship gets by the time Mars reaches the meeting
    // point, in SVG units. Game works it out from the ship's speed and the schedule
    // (setRendezvousTravel); null until then, and the marker stays off.
    this.rendezvousTravel = null;

    // Where the ship was last drawn (host pixels), plus the shape it has to be
    // inside to be seen and the matrix that takes host pixels into that shape's
    // space. What isShipOutOfView reads.
    this.shipLocal = null;
    this.visibleShape = null;
    this.shapeFromHost = null;

    // How wide the marker is drawn, in host pixels, straight off the design.
    this.rendezvousMarkerPx = options.rendezvousMarkerPx ?? 18;

    // The point everything rotates around: the sun's center, in SVG coordinates.
    this.sunOrigin = options.sunOrigin ?? "397.721 399.171";

    // How many full laps each planet makes per cycle. Both start and end the
    // cycle at their start positions, so they only line up there. Use numbers
    // with no common factor (like 3 and 2) so they line up just once per cycle.
    this.turnsPerCycle = options.turnsPerCycle ?? {earth: 2, mars: 1};

    // Where each planet starts around the sun, in degrees: 0 = top, going
    // clockwise (90 = right, 180 = bottom, 270 = left). Same value = both on the
    // same line. This is also where they return each time they line up.
    this.startAngles = options.startAngles ?? {earth: 0, mars: 0};

    // Where each planet ends up when launched (again 0 = top, clockwise). Mars
    // sets the destination (270, the 9 o'clock spot by default). Earth's end is
    // worked out from how much faster it orbits: in the same time it sweeps a
    // bigger angle than Mars, so its end can be more than half a turn from where
    // it started. See launchDeltas() for the actual movement.
    const marsTarget = options.targetAngles?.mars ?? 270;
    const speedRatio = this.turnsPerCycle.earth / this.turnsPerCycle.mars;
    const marsStartDelta = SolarSystem.shortestDelta(this.startAngles.mars, marsTarget);
    this.targetAngles = options.targetAngles ?? {
      mars: marsTarget,
      earth: SolarSystem.normAngle(this.startAngles.earth + marsStartDelta * speedRatio),
    };

    // The angle range [min, max] where each planet counts as "aligned" (same
    // 0 = top, clockwise as above). If min is bigger than max, the range wraps
    // past the top (e.g. [350, 10]). Defaults to a small window around each start.
    this.alignRanges = options.alignRanges ?? {
      earth: [this.startAngles.earth - 8, this.startAngles.earth + 8],
      mars: [this.startAngles.mars - 8, this.startAngles.mars + 8],
    };

    // Seconds for one full master cycle (one alignment to the next).
    this.cycleDuration = options.cycleDuration ?? 60;

    // -1 = counter-clockwise, 1 = clockwise.
    this.direction = options.direction ?? -1;

    // Called every time the planets reach the alignment (once per cycle).
    // Override or reassign to flash a "stop now" cue, play a sound, etc.
    this.onAlign = options.onAlign ?? null;

    // When true, draw static reference markers on each orbit (initial position
    // + alignment window) so the start angles / ranges are easy to dial in.
    this.debug = options.debug ?? false;

    this.timeline = null;

    this.init();
  }

  init() {
    // One looping cycle drives both planets. Each makes a whole number of laps in
    // the same time, so they're both back at their start positions every loop —
    // that's the moment they line up.
    this.timeline = gsap.timeline({
      repeat: -1,
      onRepeat: () => {
        if (typeof this.onAlign === "function") this.onAlign();
      },
    });

    // Current angle of each planet (degrees, 0 = top, clockwise).
    this.angles = {...this.startAngles};

    // Tweens driving the snap / transfer, tracked so reset() can cancel them.
    this.angleTweens = [];

    this.addPlanet("earth", this.turnsPerCycle.earth);
    this.addPlanet("mars", this.turnsPerCycle.mars);

    this.positionTargets();
    this.positionGhosts();
    this.buildTrajectories();
    this.positionShip();
    if (this.debug) this.drawDebug();
  }

  // Moves one planet around the sun: it does a whole number of laps and ends back
  // where it started. We track its angle (0 = top, clockwise) and put the HTML
  // planet dot at the matching spot on its orbit each frame. The SVG circle is
  // kept only to define the orbit (its center and radius).
  addPlanet(key, turns) {
    const $anchor = $(`#main-${key}`);
    if (!$anchor.length) return;

    const $planet = this.$host.find(`.planet[data-planet="${key}"]`);
    const startAngle = this.startAngles[key];

    const apply = (A) => {
      this.angles[key] = A;
      this.positionPlanet(key, A, $anchor, $planet);
    };

    apply(startAngle); // position now, so a paused timeline shows the start angle

    const state = {a: startAngle};
    this.timeline.to(state, {
      a: startAngle + 360 * turns * this.direction,
      duration: this.cycleDuration,
      ease: "none",
      onUpdate: () => apply(state.a),
    }, 0);
  }

  // Place the HTML planet dot at angle A on its orbit, sized from the anchor's
  // SVG radius (so it tracks any layout scaling).
  positionPlanet(key, A, $anchor, $planet) {
    if (!$planet || !$planet.length) return;
    const p = this.pointAtAngle(`#main-${key}`, A);
    const local = this.svgToLocal(p.x, p.y, $anchor);
    if (!local) return;

    const size = 2 * parseFloat($anchor.attr("r")) * local.scale;
    $planet.css({
      width: `${size}px`,
      height: `${size}px`,
      transform: `translate(${local.x}px, ${local.y}px) translate(-50%, -50%)`,
    });
  }

  // Place each target at its planet's start spot. The screen has to be laid out
  // first (the SVG can't be measured while it's hidden), so call this once the
  // game screen is visible, for example from Game.start().
  positionTargets() {
    for (const key of ["earth", "mars"]) {
      const $anchor = $(`#main-${key}`);
      const $target = this.$host.find(`.planet-target[data-planet="${key}"]`);
      if (!$anchor.length || !$target.length) continue;

      const p = this.pointAtAngle(`#main-${key}`, this.startAngles[key]);
      const local = this.svgToLocal(p.x, p.y, $anchor);
      if (!local) continue;

      $target.css("transform", `translate(${local.x}px, ${local.y}px) translate(-50%, -50%)`);
    }
  }

  // Place each ghost at its planet's start position (faded copy left behind when
  // the planet launches to its target). Hidden via CSS until .transferring.
  positionGhosts() {
    for (const key of ["earth", "mars"]) {
      const $anchor = $(`#main-${key}`);
      const $ghost = this.$host.find(`.planet-ghost[data-planet="${key}"]`);
      if (!$anchor.length || !$ghost.length) continue;
      this.positionPlanet(key, this.startAngles[key], $anchor, $ghost);
    }
  }

  // Earth-start position in host pixels (where the ship + arcs anchor). Null
  // until the SVG is laid out.
  earthStartLocal() {
    const $anchor = $("#main-earth");
    if (!$anchor.length) return null;
    const p = this.pointAtAngle("#main-earth", this.startAngles.earth);
    return this.svgToLocal(p.x, p.y, $anchor);
  }

  // Initial hidden placement at Earth-start (the aim/flight reposition it onto
  // the path the moment it's shown).
  positionShip() {
    const $ship = this.$host.find(".ship");
    const local = this.earthStartLocal();
    if (!$ship.length || !local) return;
    $ship.css({left: `${local.x}px`, top: `${local.y}px`});
  }

  // ---- Trajectory paths (drawn on the fly into the orbit SVG) --------------

  // Where the aim path starts: Earth's start spot, in SVG coordinates.
  earthStartPoint() {
    return this.pointAtAngle("#main-earth", this.startAngles.earth);
  }

  // Where Mars will be at the end of the trip, in SVG coordinates. This is the
  // spot the player is trying to land the path's end circle on.
  marsEndPoint() {
    return this.pointAtAngle("#main-mars", this.targetAngles.mars);
  }

  // How far the ship has to travel to meet Mars, in SVG units. All the aim
  // distances are fractions of this.
  earthToMarsDistance() {
    const a = this.earthStartPoint(), b = this.marsEndPoint();
    return Math.hypot(b.x - a.x, b.y - a.y) || 1;
  }

  // The direction a planet at point p is travelling around the sun, as a unit
  // vector in SVG coordinates (y down). Follows this.direction, so it flips with
  // the orbit.
  orbitVelocityAt(p) {
    const [sx, sy] = this.sunOrigin.split(/\s+/).map(Number);
    const rx = p.x - sx, ry = p.y - sy;
    const r = Math.hypot(rx, ry) || 1;
    const ux = rx / r, uy = ry / r;
    return this.direction === -1 ? {x: uy, y: -ux} : {x: -uy, y: ux};
  }

  // Which way the path curls, as +1 or -1. Gravity bends the ship toward the sun,
  // so the center of the curve sits on the sun's side of the launch heading: aim
  // up from Earth and the path swings left over the top, aim down and it swings
  // left under the bottom. +1 puts the center a quarter turn round from the
  // heading, -1 the other way. Aiming straight at or straight away from the sun has
  // no side to pick, which is right (nothing to curl around), and falls back to +1.
  curveDirection(start, ux, uy) {
    const [sx, sy] = this.sunOrigin.split(/\s+/).map(Number);
    // A quarter turn round from the heading (which way doesn't matter, only which
    // side of it the sun is on).
    const nx = -uy, ny = ux;
    const dot = (sx - start.x) * nx + (sy - start.y) * ny;
    return dot >= 0 ? 1 : -1;
  }

  // The launch direction for a sideways drag, as a unit vector. `turn` runs -1
  // (dragged all the way left) to 1 (all the way right). At 0 the ship leaves the
  // way Earth is already travelling, and dragging swings it round from there, one
  // way or the other, up to aimSweepDeg. With the default 180 the two ends meet, so
  // the drag covers every direction.
  aimVectorFor(turn) {
    const base = this.orbitVelocityAt(this.earthStartPoint());
    const a = turn * this.aimSweepDeg * Math.PI / 180;
    const cos = Math.cos(a), sin = Math.sin(a);
    // Turning by a positive angle goes clockwise on screen, so a drag to the right
    // swings the ship to the right.
    return {x: base.x * cos - base.y * sin, y: base.x * sin + base.y * cos};
  }

  // Work out the aim path for a drag. ux/uy is the launch heading as a unit vector
  // (screen axes, y down) and force how hard the launch is, 0 to 1. The path is a
  // circle of fixed length leaving Earth on that heading: at full force it doesn't
  // curve at all and runs dead straight, and the weaker the launch the further
  // round it bends. Returns everything the rest of the code needs to draw the path,
  // walk along it and measure against it, in SVG units:
  //   start, heading, length - where it leaves from, which way, and how far it runs
  //   sweep - how far round it goes, signed, in radians (0 for a straight line)
  //   center, radius, startAngle - the circle behind it (absent when straight)
  //   end - where the path runs out
  aimGeometry(ux, uy, force) {
    const start = this.earthStartPoint();
    const cfg = this.aimArc;
    const f = Math.max(0, Math.min(1, force));
    const length = this.earthToMarsDistance() * cfg.length;
    // Full force flies straight; anything less curls round, up to sweepMax.
    const sweepDeg = Math.min(cfg.sweepMax, 350) * (1 - f);
    const geo = {start, heading: {x: ux, y: uy}, length, sweep: 0};

    if (sweepDeg < this.aimStraightDeg) {
      geo.end = {x: start.x + ux * length, y: start.y + uy * length};
      return geo;
    }

    // The circle that fits `length` of path into that much turning.
    const phi = sweepDeg * Math.PI / 180;
    const dir = this.curveDirection(start, ux, uy);
    const radius = length / phi;
    // The center sits a quarter turn round from the heading, on the side the path
    // bends toward. Turning that way is the positive direction, so the sweep takes
    // the same sign.
    const center = {x: start.x - uy * radius * dir, y: start.y + ux * radius * dir};
    const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
    const sweep = phi * dir;
    const endAngle = startAngle + sweep;

    geo.sweep = sweep;
    geo.center = center;
    geo.radius = radius;
    geo.startAngle = startAngle;
    geo.end = {
      x: center.x + radius * Math.cos(endAngle),
      y: center.y + radius * Math.sin(endAngle),
    };
    return geo;
  }

  // The SVG path for an aim geometry: one circular arc, or a straight line when
  // there's no sweep. Being a real circle, it bends evenly the whole way along
  // rather than turning hard at the two ends and running straight through the
  // middle the way a bezier with sideways levers does.
  // `length` stops it part way, which is how the drawn path can be shorter than the
  // one the ship flies.
  trajectoryPathD(geo, length = geo.length) {
    const {start} = geo;
    const s = Math.max(0, Math.min(geo.length, length));
    const end = this.svgPointAlong(geo, s);
    if (!geo.sweep) return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;

    const largeArc = s / geo.radius > Math.PI ? 1 : 0;
    // Sweep flag 1 is the positive turning direction, the same one the sign of
    // geo.sweep uses.
    const sweepFlag = geo.sweep > 0 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${geo.radius} ${geo.radius} 0 ${largeArc} ${sweepFlag} ${end.x} ${end.y}`;
  }

  // How much of the path is drawn: up to the marker, where the line has finished
  // fading. The path carries on past it, it just isn't shown.
  visibleAimLength(geo = this.aimGeo) {
    if (!geo) return 0;
    return this.rendezvousTravel == null ? geo.length : Math.min(geo.length, this.rendezvousTravel);
  }

  // The nearest the aim path gets to a point, as {distance, length}: how close it
  // comes and how far along the path that happens. This is what decides whether a
  // launch is on target, since the path runs well past Mars and what matters is
  // whether it passes through the window on the way, not where it finally ends.
  closestApproach(p, geo = this.aimGeo) {
    if (!geo) return null;

    if (!geo.sweep) {
      // Straight: drop a perpendicular onto the line and keep it on the path.
      const along = (p.x - geo.start.x) * geo.heading.x + (p.y - geo.start.y) * geo.heading.y;
      const s = Math.max(0, Math.min(geo.length, along));
      const x = geo.start.x + geo.heading.x * s, y = geo.start.y + geo.heading.y * s;
      return {distance: Math.hypot(p.x - x, p.y - y), length: s};
    }

    // Curved: the nearest point on the whole circle is straight out from its
    // center, so the only question is whether the path has swept that far round yet.
    const dist = Math.hypot(p.x - geo.center.x, p.y - geo.center.y);
    const dir = geo.sweep > 0 ? 1 : -1;
    const turned = Math.atan2(p.y - geo.center.y, p.x - geo.center.x) - geo.startAngle;
    const forward = SolarSystem.normRadians(turned * dir); // how far along, 0 to a full turn
    const phi = Math.abs(geo.sweep);
    if (forward <= phi) {
      return {distance: Math.abs(dist - geo.radius), length: forward * geo.radius};
    }

    // It swings past before getting there, so the nearest it comes is one of the ends.
    const toStart = Math.hypot(p.x - geo.start.x, p.y - geo.start.y);
    const toEnd = Math.hypot(p.x - geo.end.x, p.y - geo.end.y);
    return toStart <= toEnd ? {distance: toStart, length: 0} : {distance: toEnd, length: geo.length};
  }

  // The nearest the aim path gets to Mars's arrival point. Null before the first aim.
  closestApproachToMars() {
    return this.closestApproach(this.marsEndPoint());
  }

  // How far a planet's orbit runs from the sun, in SVG units.
  orbitRadius(selector) {
    const {cx, cy, sx, sy} = this.planetGeometry(selector);
    return Math.hypot(cx - sx, cy - sy);
  }

  // The closest the aim path gets to the sun over the stretch the ship actually
  // flies, in SVG units. Being a circle, the distance to the sun only rises and
  // falls once around it, so the nearest point is either the one straight in line
  // with the sun (when the path sweeps past it) or one of the two ends.
  sunApproach(geo, length = geo.length) {
    if (!geo) return Infinity;
    const [sx, sy] = this.sunOrigin.split(/\s+/).map(Number);
    const s = Math.max(0, Math.min(geo.length, length));

    if (!geo.sweep) {
      // Straight: drop a perpendicular onto the line and keep it on the path.
      const along = (sx - geo.start.x) * geo.heading.x + (sy - geo.start.y) * geo.heading.y;
      const t = Math.max(0, Math.min(s, along));
      return Math.hypot(sx - (geo.start.x + geo.heading.x * t), sy - (geo.start.y + geo.heading.y * t));
    }

    const dir = geo.sweep > 0 ? 1 : -1;
    const toSun = Math.atan2(sy - geo.center.y, sx - geo.center.x);
    const forward = SolarSystem.normRadians((toSun - geo.startAngle) * dir);
    if (forward <= s / geo.radius) {
      return Math.abs(geo.radius - Math.hypot(sx - geo.center.x, sy - geo.center.y));
    }

    const end = this.svgPointAlong(geo, s);
    return Math.min(
      Math.hypot(sx - geo.start.x, sy - geo.start.y),
      Math.hypot(sx - end.x, sy - end.y),
    );
  }

  // True when the path keeps outside Earth's orbit the whole way to Mars. Earth's
  // orbit is the floor of the trip: the ship leaves from a point on it already
  // moving at Earth's speed, so a path that dives under it is falling toward the
  // sun, not crossing to Mars. The one launch that reaches Mars without ever going
  // under runs tangent to the orbit at Earth and arcs over the top of the sun,
  // which is the transfer this whole screen is about.
  staysOutsideEarthOrbit(geo, length = geo?.length) {
    if (!geo) return false;
    const floor = this.orbitRadius("#main-earth") - this.pxToSvg(this.innerBoundSlackPx);
    return this.sunApproach(geo, length) >= floor;
  }

  // The target circle's radius in SVG units, the same one the ring is drawn at, so
  // what's on screen is what these tests measure against.
  destinationRadiusSvg() {
    return this.pxToSvg(this.destinationRadiusPx);
  }

  // True when a point is inside the target circle. One test behind both halves of
  // the game: asked of the marker while aiming, of the ship and Mars while flying.
  isInDestination(p) {
    if (!p) return false;
    const m = this.marsEndPoint();
    return Math.hypot(p.x - m.x, p.y - m.y) <= this.destinationRadiusSvg();
  }

  // Where Mars is right now. Unlike marsEndPoint this follows the planet as it moves.
  marsCurrentPoint() {
    return this.pointAtAngle("#main-mars", this.angles?.mars ?? this.startAngles.mars);
  }

  // True while Mars is crossing the target circle. That pass is the whole window
  // the capture can happen in.
  isMarsInDestination() {
    return this.isInDestination(this.marsCurrentPoint());
  }

  // Where the ship is at travel fraction t of the aim path, in SVG coordinates.
  aimPointAtProgress(t) {
    if (!this.aimGeo) return null;
    return this.svgPointAlong(this.aimGeo, t * this.aimGeo.length);
  }

  // Where the ship will be when Mars arrives: the spot the marker is drawn on.
  rendezvousPoint() {
    if (!this.aimGeo || this.rendezvousTravel == null) return null;
    return this.svgPointAlong(this.aimGeo, this.rendezvousTravel);
  }

  // How long the whole aim path runs, in SVG units. This never changes with the
  // force; only the shape does.
  aimPathLength() {
    return this.aimGeo ? this.aimGeo.length : 0;
  }

  // A point a given distance along an aim geometry, in SVG units. This works off
  // the geometry itself rather than the drawn path, so it can measure a path that
  // isn't on screen.
  svgPointAlong(geo, length) {
    if (!geo) return null;
    const s = Math.max(0, Math.min(geo.length, length));
    if (!geo.sweep) {
      return {x: geo.start.x + geo.heading.x * s, y: geo.start.y + geo.heading.y * s};
    }
    const a = geo.startAngle + (s / geo.radius) * Math.sign(geo.sweep);
    return {x: geo.center.x + geo.radius * Math.cos(a), y: geo.center.y + geo.radius * Math.sin(a)};
  }

  // Wrap an angle into 0 to a full turn, in radians.
  static normRadians(a) {
    const turn = Math.PI * 2;
    return ((a % turn) + turn) % turn;
  }

  // Build the (empty) aim path and the destination ring on Mars as SVG in the orbit
  // SVG. The aim path gets its shape from the drag (setAimPath). Both start hidden
  // (their group's opacity), revealed by is-active.
  buildTrajectories() {
    const svg = $("#main-earth").get(0)?.ownerSVGElement;
    if (!svg) return;
    this.clearTrajectories();

    this.aimGeo = null;
    const layer = SolarSystem.svgEl("g", {id: "trajectory-layer"});

    // The zone of good launches, first so everything else draws over it.
    layer.appendChild(this.buildGoodZoneGradient());
    layer.appendChild(SolarSystem.svgEl("path", {class: "trajectory-good-zone", d: "", fill: "url(#traj-good-zone-fade)"}));

    // The one path the player aims. It has no shape yet, so it's drawn as a zero
    // length line on Earth and reshaped the moment the press starts.
    const start = this.earthStartPoint();
    layer.appendChild(this.buildTrajectoryGroup("aim", `M ${start.x} ${start.y} L ${start.x} ${start.y}`));

    // The window the path has to pass through to count as on target, drawn on
    // Mars's arrival spot so the player can see what they're aiming at.
    layer.appendChild(this.buildDestinationMarker());

    // Where the ship will be when Mars gets there, drawn on the path itself.
    layer.appendChild(this.buildRendezvousMarker());

    svg.appendChild(layer);
    this.drawGoodZone();
  }

  // Build one trajectory group: a dashed mask, the line shown through it and a solid
  // trail that fills in behind the ship. The dashed line is painted with a gradient
  // that dies away toward the far end instead of stopping dead, so the path reads as
  // trailing off rather than as a line with an end. That's only how it looks: the
  // path really does carry on, and the ship follows it right out past where it stops
  // being visible.
  //
  // 2px, 8/8 dashes, straight off the design. The mask is what gives that width: the
  // visible path is drawn far thicker and clipped to the dashes, which is what lets
  // the fade gradient paint them. Edit the mask, not the reveal.
  //
  // Two paths, not one: the drawn parts stop at the marker, the flight path holds
  // the whole geometry and is never painted. The ship measures along the flight one,
  // so what's shown can be shortened without moving it.
  buildTrajectoryGroup(dir, d) {
    const maskId = `traj-${dir}-mask`;
    const fadeId = `traj-${dir}-fade`;
    const tipFadeId = `traj-${dir}-tip-fade`;
    const g = SolarSystem.svgEl("g", {class: "trajectory", "data-dir": dir});

    // The full path, for measuring only: no stroke, no fill, nothing to see.
    g.appendChild(SolarSystem.svgEl("path", {class: "trajectory-flight", d, fill: "none", stroke: "none"}));

    // The region is spelled out because the default is the SVG's own box plus 10%,
    // and a long aim reaches past that: the dashes were cut off in a straight line
    // there while the marker, which isn't masked, carried on showing.
    const mask = SolarSystem.svgEl("mask", {
      id: maskId,
      "mask-type": "alpha",
      maskUnits: "userSpaceOnUse",
      x: -10000,
      y: -10000,
      width: 20000,
      height: 20000,
    });
    mask.appendChild(SolarSystem.svgEl("path", {class: "trajectory-mask", d, fill: "none", stroke: "#F4EEE6", "stroke-width": 2, "stroke-dasharray": "8 8"}));
    g.appendChild(mask);
    g.appendChild(this.buildFadeGradient(fadeId));

    const masked = SolarSystem.svgEl("g", {mask: `url(#${maskId})`});
    masked.appendChild(SolarSystem.svgEl("path", {
      class: "trajectory-reveal",
      d,
      fill: "none",
      stroke: `url(#${fadeId})`,
      "stroke-width": 6,
    }));
    g.appendChild(masked);
    // Solid trail drawn over the dashed line: filled in behind the ship, dashes
    // showing ahead of it, so the line firms up as the ship travels. Unlike the
    // dashes it carries the whole geometry and takes no fade, so it keeps drawing at
    // full strength wherever the ship goes, including past the marker where the
    // dashed line has already faded out. Its colour comes from CSS. JS grows it;
    // "0 100000" means nothing shows until then.
    g.appendChild(SolarSystem.svgEl("path", {class: "trajectory-trail", d, fill: "none", "stroke-width": 3, "stroke-dasharray": "0 100000"}));

    // The last stretch of that trail, for a flight that stops on the path instead
    // of leaving the screen. It is the same line at the same width, drawn over the
    // end of the trail and painted with a gradient that runs out to nothing, so
    // the trail can taper off instead of ending on a straight cut. Down until
    // fadeTrailTip raises it.
    g.appendChild(this.buildTipFadeGradient(tipFadeId));
    g.appendChild(SolarSystem.svgEl("path", {
      class: "trajectory-trail-tip",
      d,
      fill: "none",
      stroke: `url(#${tipFadeId})`,
      "stroke-width": 3,
      "stroke-dasharray": "0 100000",
    }));

    return g;
  }

  // The gradient painting that tip: the trail's own colour, held to the end and
  // then gone. Where it starts letting go is what fadeTrailTip animates, from the
  // very end (a hard cut, which is what the trail looks like without any of this)
  // back along the tip until the whole of it is a fade.
  buildTipFadeGradient(id) {
    const grad = SolarSystem.svgEl("linearGradient", {id, gradientUnits: "userSpaceOnUse", x1: 0, y1: 0, x2: 0, y2: 0});

    grad.appendChild(SolarSystem.svgEl("stop", {class: "trajectory-tip-stop", offset: 1, "stop-opacity": 1}));
    grad.appendChild(SolarSystem.svgEl("stop", {class: "trajectory-tip-stop", offset: 1, "stop-opacity": 0}));

    return grad;
  }

  // The gradient that fades the far end out: between the two ends of the drawn part,
  // full strength up to fadeStart and gone by the end, where the marker sits. The
  // stops take their colour from CSS; the attribute here is only the fallback.
  buildFadeGradient(id) {
    const grad = SolarSystem.svgEl("linearGradient", {id, gradientUnits: "userSpaceOnUse", x1: 0, y1: 0, x2: 0, y2: 0});
    const stops = [[0, 1], [this.aimFadeStart, 1], [1, 0]];
    for (const [offset, opacity] of stops) {
      grad.appendChild(SolarSystem.svgEl("stop", {
        class: "trajectory-stop",
        offset,
        "stop-color": "#F4EEE6",
        "stop-opacity": opacity,
      }));
    }
    return grad;
  }

  // Mark the aim as leaving Earth downwards, against the way Earth is already going
  // round the sun. Only markUpstream puts that in colour; otherwise it draws nothing,
  // since saying it outright hands over the reason the launch will fail.
  setAimUpstream(upstream) {
    const on = !!upstream && this.markUpstream;
    const el = $(`#trajectory-layer .trajectory[data-dir="aim"]`).get(0);
    if (el) el.classList.toggle("is-upstream", on);

    // The marker takes it too. The system is mirrored about the Earth-sun-Mars line,
    // so every aim that meets Mars over the top has a twin underneath that lands the
    // marker dead centre and is still refused.
    const marker = $("#trajectory-layer .trajectory-rendezvous").get(0);
    if (marker) marker.classList.toggle("is-upstream", on);
  }

  // The ring on Mars's arrival spot marking where the launch has to land. Its
  // radius is the tolerance in host pixels converted to SVG units, so it always
  // matches the real hit test. Hidden until aiming turns it on.
  // Two circles on the same spot, one dashed and one solid, so going solid on a
  // launch that meets Mars can cross-fade: the dash pattern itself can't animate.
  buildDestinationMarker() {
    const p = this.marsEndPoint();
    const r = this.pxToSvg(this.destinationRadiusPx);
    const g = SolarSystem.svgEl("g", {class: "trajectory-destination"});
    ["dashed", "solid"].forEach((kind) => {
      g.appendChild(SolarSystem.svgEl("circle", {
        class: `trajectory-destination-${kind}`,
        cx: p.x,
        cy: p.y,
        r,
      }));
    });
    return g;
  }

  // Where the ship will have got to at the moment Mars arrives: one solid disc on
  // the line. No position yet, since the path it rides doesn't exist; setAimPath
  // puts it where it belongs.
  buildRendezvousMarker() {
    const g = SolarSystem.svgEl("g", {class: "trajectory-rendezvous"});
    g.appendChild(SolarSystem.svgEl("circle", {
      class: "trajectory-rendezvous-dot",
      cx: 0,
      cy: 0,
      r: this.pxToSvg(this.rendezvousMarkerPx / 2),
    }));
    return g;
  }

  // Say how far the ship travels before Mars arrives, in SVG units. Game owns the
  // number (it's the ship's speed over the schedule) and only has to set it once
  // per launch, since neither changes while aiming.
  setRendezvousTravel(length) {
    this.rendezvousTravel = length;
    this.positionRendezvousMarker();
    // The drawn path stops at the marker, so moving the marker reshapes it.
    if (this.aimGeo) this.setAimPath(this.aimGeo);
  }

  // Move the marker onto the current aim path, at the travel distance above. The
  // path always runs well past that distance, so it never has to sit at an end.
  positionRendezvousMarker() {
    const g = $("#trajectory-layer .trajectory-rendezvous").get(0);
    if (!g || !this.aimGeo || this.rendezvousTravel == null) return;
    const p = this.svgPointAlong(this.aimGeo, this.rendezvousTravel);
    if (p) g.setAttribute("transform", `translate(${p.x} ${p.y})`);
  }

  // How many SVG units a screen pixel is worth, from the SVG's own screen matrix.
  pxToSvg(px) {
    const local = this.svgToLocal(0, 0, $("#main-earth"));
    const scale = local?.scale || 1;
    return px / scale;
  }

  // Set the radius (host pixels) of the destination ring, i.e. how close the path's
  // end has to get to Mars to count. Game owns the number so it can read it off the
  // capture indicator.
  setDestinationRadius(px) {
    this.destinationRadiusPx = px;
    $("#trajectory-layer .trajectory-destination circle").attr("r", this.pxToSvg(px));
  }

  // Let the game show the destination ring (true) or go back to hiding it. Takes
  // effect on the next showDestination; hiding also takes the ring off right away.
  setTargetRingRevealed(on) {
    this.targetRingRevealed = !!on;
    if (!on && !this.showTargetRing) {
      $("#trajectory-layer .trajectory-destination, #trajectory-layer .trajectory-good-zone").removeClass("is-active");
    }
  }

  // Turn a set of launches, each flown `travel` along its path, into the good zone.
  // The paths all leave Earth and all fly the same distance, so they can be cut
  // into matching slices: the same fraction of the way along each. Every slice is
  // measured across the direction the bundle is heading there, and its two
  // outermost points are the edges of the zone at that slice, so the two edges can
  // never cross.
  //
  // Traced as is, that outline turns a corner wherever one path takes over from
  // another as the outermost, so how far each edge sits from the middle of the
  // bundle is smoothed along the way (goodZoneSmoothing). The end is rounded off
  // inside the target circle rather than cut straight.
  setGoodZone(geos, travel) {
    this.goodZoneD = null;
    this.goodZoneFade = null;
    if (geos.length) {
      const slices = 200;
      const points = geos.map((geo) => {
        const row = [];
        for (let k = 0; k <= slices; k++) row.push(this.svgPointAlong(geo, (travel * k) / slices));
        return row;
      });

      const means = [];
      for (let k = 0; k <= slices; k++) {
        let x = 0, y = 0;
        points.forEach((row) => {
          x += row[k].x;
          y += row[k].y;
        });
        means.push({x: x / points.length, y: y / points.length});
      }

      const normals = [], his = [], los = [];
      for (let k = 0; k <= slices; k++) {
        const a = means[Math.max(0, k - 1)], b = means[Math.min(slices, k + 1)];
        const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
        const n = {x: -(b.y - a.y) / len, y: (b.x - a.x) / len};
        const m = means[k];

        let lo = Infinity, hi = -Infinity;
        points.forEach((row) => {
          const offset = (row[k].x - m.x) * n.x + (row[k].y - m.y) * n.y;
          lo = Math.min(lo, offset);
          hi = Math.max(hi, offset);
        });
        normals.push(n);
        his.push(hi);
        los.push(lo);
      }

      const hiSmooth = SolarSystem.smoothed(his, this.goodZoneSmoothing * slices);
      const loSmooth = SolarSystem.smoothed(los, this.goodZoneSmoothing * slices);
      const edge = (offsets) => means.map((m, k) => ({x: m.x + normals[k].x * offsets[k], y: m.y + normals[k].y * offsets[k]}));
      const outer = edge(hiSmooth), inner = edge(loSmooth);

      const forward = {x: means[slices].x - means[slices - 1].x, y: means[slices].y - means[slices - 1].y};
      const line = (pts) => pts.map((p) => `${p.x} ${p.y}`).join(" L ");
      this.goodZoneD = `M ${line(outer)} ${this.goodZoneCap(outer[slices], inner[slices], forward)} L ${line(inner.reverse())} Z`;

      // The fade runs straight from Earth to the end of the zone, the way the aim
      // path's does. The zone is curved, so the point where it's gone is not that
      // far along the straight line: it's where that far along the paths lands on it.
      const start = means[0], end = means[slices];
      const fade = means[Math.round(slices * this.goodZoneFadeAt)];
      const dx = end.x - start.x, dy = end.y - start.y;
      const along = ((fade.x - start.x) * dx + (fade.y - start.y) * dy) / (dx * dx + dy * dy || 1);
      this.goodZoneFade = {
        x1: start.x,
        y1: start.y,
        x2: end.x,
        y2: end.y,
        offset: Math.max(0, Math.min(1, along)),
      };
    }
    this.drawGoodZone();
  }

  // A running average over `radius` values either side. The window shrinks near the
  // ends so it never reaches past them, which keeps the first and last values where
  // they were: the zone still starts on Earth and ends where the paths do.
  static smoothed(values, radius) {
    const last = values.length - 1;
    return values.map((v, i) => {
      const w = Math.floor(Math.min(radius, i, last - i));
      let sum = 0;
      for (let j = i - w; j <= i + w; j++) sum += values[j];
      return sum / (2 * w + 1);
    });
  }

  // Put the kept zone and its fade onto the trajectory layer, which may have been
  // rebuilt since it was worked out.
  drawGoodZone() {
    $("#trajectory-layer .trajectory-good-zone").attr("d", this.goodZoneD || "");
    if (!this.goodZoneFade) return;

    const {x1, y1, x2, y2, offset} = this.goodZoneFade;
    $("#traj-good-zone-fade").attr({x1, y1, x2, y2});
    const $stops = $("#traj-good-zone-fade .trajectory-good-zone-stop");
    $stops.first().attr("stop-opacity", this.goodZoneStrength);
    $stops.last().attr("offset", offset);
  }

  // The gradient painting the good zone: goodZoneStrength at Earth, gone at
  // goodZoneFadeAt. drawGoodZone lines it up; the colour comes from CSS.
  buildGoodZoneGradient() {
    const grad = SolarSystem.svgEl("linearGradient", {id: "traj-good-zone-fade", gradientUnits: "userSpaceOnUse", x1: 0, y1: 0, x2: 0, y2: 0});
    const stops = [[0, this.goodZoneStrength], [this.goodZoneFadeAt, 0]];
    for (const [offset, opacity] of stops) {
      grad.appendChild(SolarSystem.svgEl("stop", {
        class: "trajectory-good-zone-stop",
        offset,
        "stop-color": "#A5F2A6",
        "stop-opacity": opacity,
      }));
    }
    return grad;
  }

  // The round end of the good zone, from the end of one edge to the end of the
  // other: a half circle bulging the way the paths are heading (forward), like a
  // round line cap, so the zone doesn't stop on a straight cut.
  goodZoneCap(p1, p2, forward) {
    const cx = p2.x - p1.x, cy = p2.y - p1.y;
    const r = Math.hypot(cx, cy) / 2;
    if (r < 0.5) return `L ${p2.x} ${p2.y}`;

    // Sweep flag 1 bulges to the (cy, -cx) side of the line between the ends.
    const sweepFlag = cy * forward.x - cx * forward.y > 0 ? 1 : 0;
    return `A ${r} ${r} 0 0 ${sweepFlag} ${p2.x} ${p2.y}`;
  }

  // Show or hide the destination ring and say how good the launch is. All three
  // looks read off the marker: faint (outside the ring), lit (inside it but refused
  // for another reason, in practice going against Earth's motion) and solid.
  showDestination(visible, aligned = false, onSchedule = false) {
    const el = $("#trajectory-layer .trajectory-destination").get(0);
    if (!el) return;
    const ringShown = !!(visible && (this.showTargetRing || this.targetRingRevealed));
    el.classList.toggle("is-active", ringShown);

    // The good zone is part of the same hint, so it comes and goes with the ring.
    const zone = $("#trajectory-layer .trajectory-good-zone").get(0);
    if (zone) zone.classList.toggle("is-active", ringShown);
    el.classList.toggle("is-aligned", !!(visible && aligned));
    el.classList.toggle("is-hit", !!(visible && aligned && onSchedule));

    // The marker is the other half of the same reading, so it comes and goes with
    // the ring and lights on the same launch.
    const marker = $("#trajectory-layer .trajectory-rendezvous").get(0);
    if (!marker) return;
    marker.classList.toggle("is-active", !!(visible && this.rendezvousTravel != null));
    marker.classList.toggle("is-hit", !!(visible && aligned && onSchedule));
  }

  // Reshape the aim path from an aimGeometry and show it. The dashed mask, the line
  // and the trail all follow the same curve, and the fade gradient is stretched
  // between the two ends. The trail goes back to empty, since the path it was drawn
  // along has just changed.
  setAimPath(geo) {
    const $g = $(`#trajectory-layer .trajectory[data-dir="aim"]`);
    const el = $g.get(0);
    if (!el || !geo) return;

    this.aimGeo = geo;
    // The flight path and the trail behind the ship both carry the whole geometry,
    // and so does the tip that lies on the end of that trail; only the dashed line
    // ahead stops at the marker.
    $g.find(".trajectory-flight, .trajectory-trail, .trajectory-trail-tip").attr("d", this.trajectoryPathD(geo));

    const shown = this.visibleAimLength(geo);
    const d = this.trajectoryPathD(geo, shown);
    $g.find(".trajectory-mask, .trajectory-reveal").attr("d", d);
    $g.find(".trajectory-trail").get(0).style.strokeDasharray = "0 100000";
    $g.find(".trajectory-trail-tip").get(0).style.strokeDasharray = "0 100000";
    // The gradient is straight and the path is a curve, so this only lines up
    // while the drawn part stays inside a half turn: any further and it doubles
    // back along the axis and fades unevenly. The launches worth reading stay well
    // inside that (the one that meets Mars bends about 160 degrees), and only the
    // bottom of the force range, where the path curls into a loop nobody is going
    // to release on, goes past it.
    const end = this.svgPointAlong(geo, shown);
    $g.find("#traj-aim-fade").attr({
      x1: geo.start.x,
      y1: geo.start.y,
      x2: end.x,
      y2: end.y,
    });
    el.classList.add("is-active");

    // The path just changed shape, so the meeting-point marker has moved with it.
    this.positionRendezvousMarker();
  }

  // Hide the aim path (no drag, or the aim was cleared). Drops the upstream marking
  // with it, so a fresh aim never starts out wearing the last one's warning.
  hideAimPath() {
    const el = $(`#trajectory-layer .trajectory[data-dir="aim"]`).get(0);
    if (el) el.classList.remove("is-active", "is-upstream");
  }

  clearTrajectories() {
    $("#trajectory-layer").remove();
  }

  // ---- Ship along the aim arc ---------------------------------------------

  // The path the ship flies: the whole geometry, drawn or not. Everything that
  // measures the arc goes through this, never through the visible line.
  arcPath(dir) {
    return $(`#trajectory-layer .trajectory[data-dir="${dir}"] .trajectory-flight`).get(0);
  }

  // The solid trail path that fills in behind the moving ship.
  arcTrail(dir) {
    return $(`#trajectory-layer .trajectory[data-dir="${dir}"] .trajectory-trail`).get(0);
  }

  // Hide every trail (for example when the aim or flight clears).
  clearTrails() {
    document.querySelectorAll("#trajectory-layer .trajectory-trail, #trajectory-layer .trajectory-trail-tip").forEach((p) => {
      p.style.strokeDasharray = "0 100000";
    });
  }

  // Tapers the end of the flown trail, for a flight that came to rest on the path
  // rather than running off the screen: without this it stops on a straight cut,
  // which reads as the line having been sliced rather than run out.
  //
  // The trail keeps every pixel it was drawn to. Its last `fraction` is simply
  // handed over to the tip path lying on top of it, which carries the gradient,
  // and the handover cannot be seen because the gradient starts out solid: it is
  // the gradient letting go, back along the tip, that plays over `duration`.
  fadeTrailTip(dir, fraction = 0.02, duration = 0.4) {
    const path = this.arcPath(dir);
    const trail = this.arcTrail(dir);
    const tip = this.arcTrailTip(dir);
    if (!path || !trail || !tip) return null;

    // How much of the trail has been drawn, which is where its end is: the first
    // number of the dash pattern the flight left on it.
    const drawn = parseFloat(trail.style.strokeDasharray) || 0;
    const tipLength = drawn * fraction;
    if (tipLength <= 0) return null;

    const from = path.getPointAtLength(drawn - tipLength);
    const to = path.getPointAtLength(drawn);
    const gradient = $(`#trajectory-layer .trajectory[data-dir="${dir}"] #traj-${dir}-tip-fade`).get(0);
    if (!gradient) return null;

    // The tip is short enough that running the gradient straight between its two
    // ends follows the curve closely enough to read as following it.
    gradient.setAttribute("x1", from.x);
    gradient.setAttribute("y1", from.y);
    gradient.setAttribute("x2", to.x);
    gradient.setAttribute("y2", to.y);

    // The tip takes the trail's colour from the trail itself, since that is set in
    // CSS and a gradient stop cannot inherit it.
    const color = window.getComputedStyle(trail).stroke;
    gradient.querySelectorAll(".trajectory-tip-stop").forEach((stop) => stop.setAttribute("stop-color", color));

    // Nothing, then the stretch before the end, then the tip, then nothing again.
    trail.style.strokeDasharray = `${drawn - tipLength} ${path.getTotalLength()}`;
    tip.style.strokeDasharray = `0 ${drawn - tipLength} ${tipLength} 100000`;

    // Both stops start at the very end, which is the hard cut the trail had. The
    // first one walks back to the start of the tip and the fade opens out behind it.
    const stop = gradient.querySelector(".trajectory-tip-stop");
    const state = {offset: 1};

    return gsap.to(state, {
      offset: 0,
      duration,
      ease: "power2.out",
      onUpdate: () => stop.setAttribute("offset", state.offset)
    });
  }

  // The gradient painted copy of the trail's last stretch, see fadeTrailTip.
  arcTrailTip(dir) {
    return $(`#trajectory-layer .trajectory[data-dir="${dir}"] .trajectory-trail-tip`).get(0) || null;
  }

  // A point a given distance along the arc, in host pixels.
  arcPointLocal(dir, s) {
    const path = this.arcPath(dir);
    if (!path) return null;
    const p = path.getPointAtLength(s);
    return this.svgToLocal(p.x, p.y, $("#main-earth"));
  }

  // The travel direction (in degrees) at a given distance along the arc, pointing
  // from Earth toward the end.
  arcTangentAngle(dir, s) {
    const path = this.arcPath(dir);
    if (!path) return 0;
    const L = path.getTotalLength();
    const eps = Math.min(5, L);
    const a = path.getPointAtLength(Math.min(s, L - eps));
    const b = path.getPointAtLength(Math.min(s + eps, L));
    return Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
  }

  // The arc's launch direction at the Earth end (to aim the ship while dragging).
  aimTangentAngle(dir) {
    return this.arcTangentAngle(dir, 0);
  }

  // While aiming: the ship stays centered on Earth's target and only turns to
  // point along the aimed arc; it never moves off that spot or changes size.
  setShipAim(dir) {
    const local = this.earthStartLocal();
    if (!local) return;
    const ang = this.aimTangentAngle(dir) + this.shipRotationOffset;
    this.$host.find(".ship").css({
      left: `${local.x}px`,
      top: `${local.y}px`,
      transform: `translate(-50%, -50%) rotate(${ang}deg) scale(1, 1)`,
    });
    this.shipRotation = ang;
    this.shipScaleX = 1;
    this.shipScaleY = 1;
  }

  // Place the ship somewhere along the arc, where t goes 0 (launch) to 1 (end).
  // The launch point is shipStartOffset down the path. It's turned to point along
  // the arc and scaled by a multiple of its CSS size (the flight ramps this up and
  // back down, see flightScale). scaleY defaults to scaleX for a uniform scale;
  // pass both to squash it non-uniformly.
  // Pass showTrail to fill the trail behind it.
  setShipAlong(dir, t, scaleX = 1, scaleY = scaleX, showTrail = true) {
    const path = this.arcPath(dir);
    if (!path) return;
    const len = path.getTotalLength();
    const s0 = Math.min(this.shipStartOffset, len);
    const s = s0 + t * (len - s0);
    const pt = this.arcPointLocal(dir, s);
    if (!pt) return;
    const ang = this.arcTangentAngle(dir, s) + this.shipRotationOffset;
    this.$host.find(".ship").css({
      left: `${pt.x}px`,
      top: `${pt.y}px`,
      transform: `translate(-50%, -50%) rotate(${ang}deg) scale(${scaleX}, ${scaleY})`,
    });
    // Remember where it was drawn (host pixels), so isShipOutOfView can answer
    // without measuring anything, and its transform bits so centerShipOnMars can
    // tween them.
    this.shipLocal = pt;
    this.shipRotation = ang;
    this.shipScaleX = scaleX;
    this.shipScaleY = scaleY;
    // Fill the trail from Earth up to where the ship is. The gap is the whole path,
    // not just the part being travelled, so the dashes never start over.
    if (showTrail) {
      const trail = this.arcTrail(dir);
      if (trail) trail.style.strokeDasharray = `${s} ${path.getTotalLength()}`;
    }
  }

  // Fly the ship along the path over the given time (used during the trip). It rides
  // the path the whole way, at one steady pace, so the trip reads as a single even
  // motion. onProgress, if given, is called each frame with the travel fraction (0
  // to 1), so callers can time cues to where the ship is. landing tunes the flight:
  //   launchScale - how much bigger the ship gets for the rest of the flight, as a
  //                 multiple of its CSS size (e.g. 1.5). It ramps up to this as it
  //                 leaves Earth and stays there, so resizing the ship for real is
  //                 a CSS change, not a change here.
  //   scaleUpSec - how long that ramp takes, in seconds. Capped at the flight's own
  //                length so a short flight still reaches full size.
  // It always flies the whole path: nothing out there stops the ship, so reaching
  // Mars is something the capture challenge does to it, not something the flight
  // knows about.
  flyShipAlongArc(dir, duration = 2, onComplete = null, onProgress = null, landing = null) {
    const launchScale = landing?.launchScale ?? 1;
    const scaleUpSec = landing?.scaleUpSec ?? this.shipScaleUpSec;
    const scaleUpFrac = duration > 0 ? Math.min(1, scaleUpSec / duration) : 0;

    const state = {t: 0};
    const tween = gsap.to(state, {
      t: 1,
      duration,
      ease: "linear",
      onUpdate: () => {
        // Grow leaving Earth, then hold that size for the rest of the flight.
        const s = this.flightScale(state.t, launchScale, scaleUpFrac);
        this.setShipAlong(dir, state.t, s, s, true);
        if (typeof onProgress === "function") onProgress(state.t);
      },
      onComplete,
    });
    this.shipTween = tween;
    this.angleTweens.push(tween); // tracked so reset() can cancel it
    return tween;
  }

  // The ship's scale at travel fraction t: it grows from 1 (its resting size) to
  // peak over the first scaleUpFrac of the flight as it leaves Earth, then holds
  // peak for the rest. It never scales back down on its own.
  flightScale(t, peak, scaleUpFrac) {
    if (peak === 1 || scaleUpFrac <= 0) return peak;
    if (t < scaleUpFrac) return 1 + (peak - 1) * (t / scaleUpFrac);
    return peak;
  }

  // Work out what counts as visible and keep it. That's the morphing background
  // blob: every screen is clipped to it (#bg-morph-clip in main.scss), so the ship
  // is gone well before it reaches the edge of the screen. We measure against
  // #bg-morph, the rendered copy of the same shape, because a path inside a clipPath
  // has no screen matrix to ask.
  //
  // Once at launch, not per frame: the flight already writes to the DOM each frame,
  // so re-measuring there would force a layout read for an answer that can't change.
  cacheVisibleShape(selector = "#bg-morph") {
    this.visibleShape = null;
    this.shapeFromHost = null;

    const path = document.querySelector(selector);
    const host = this.$host.get(0);
    if (!path || !host || typeof path.isPointInFill !== "function") return;
    const ctm = path.getScreenCTM();
    if (!ctm) return;

    // Host pixels -> viewport -> the shape's own user space.
    const hostRect = host.getBoundingClientRect();
    this.visibleShape = path;
    this.shapeFromHost = ctm.inverse().translate(hostRect.left, hostRect.top);
  }

  // True when a host-pixel point falls inside the visible shape.
  isPointVisible(x, y) {
    if (!this.visibleShape || !this.shapeFromHost) return true;
    return this.visibleShape.isPointInFill(new DOMPoint(x, y).matrixTransform(this.shapeFromHost));
  }

  // True when the ship is wholly outside the visible shape, testing the corners of a
  // `margin` box around it so it counts as gone only once the last of it has been
  // clipped away. False until a flight has drawn it and false without a shape, so a
  // missing measurement can't cut a trip short.
  isShipOutOfView(margin = 0) {
    const p = this.shipLocal;
    if (!this.visibleShape || !p) return false;
    const corners = margin > 0
      ? [[-margin, -margin], [margin, -margin], [margin, margin], [-margin, margin]]
      : [[0, 0]];
    return corners.every(([dx, dy]) => !this.isPointVisible(p.x + dx, p.y + dy));
  }

  // Cancel just the ship's arc flight, leaving the planet transfer running. Used
  // when a successful capture redirects the ship onto Mars.
  killShipFlight() {
    if (this.shipTween) {
      this.shipTween.kill();
      this.shipTween = null;
    }
  }

  // Mars's end (target) position in host pixels, where it settles at the end of
  // the transfer. Null until the SVG is laid out.
  marsEndLocal() {
    const $anchor = $("#main-mars");
    if (!$anchor.length) return null;
    const p = this.pointAtAngle("#main-mars", this.targetAngles.mars);
    return this.svgToLocal(p.x, p.y, $anchor);
  }

  // Mars's position right now, in host pixels. Null until the SVG is laid out.
  marsCurrentLocal() {
    const $anchor = $("#main-mars");
    if (!$anchor.length) return null;
    const p = this.marsCurrentPoint();
    return this.svgToLocal(p.x, p.y, $anchor);
  }

  // Ease the ship onto Mars, wherever Mars happens to be: not the middle of the
  // target circle, which is only where it was going to be. Straightens up on the way
  // and, with endScale {x, y}, finishes shrinking to it.
  centerShipOnMars(duration = 0.45, endScale = null, onComplete = null, ease = "power2.out") {
    const $ship = this.$host.find(".ship");
    const local = this.marsCurrentLocal();
    if (!$ship.length || !local) {
      if (typeof onComplete === "function") onComplete();
      return null;
    }

    // Start from the ship's current position and transform, straightening the
    // heading the short way round (bring it into -180..180 so it doesn't spin).
    let rot = this.shipRotation ?? 0;
    rot = ((rot + 180) % 360 + 360) % 360 - 180;
    const state = {
      x: parseFloat($ship.css("left")) || local.x,
      y: parseFloat($ship.css("top")) || local.y,
      rot,
      sx: this.shipScaleX ?? 1,
      sy: this.shipScaleY ?? 1,
    };

    const tween = gsap.to(state, {
      x: local.x,
      y: local.y,
      rot: 0,
      sx: endScale ? endScale.x : state.sx,
      sy: endScale ? endScale.y : state.sy,
      duration,
      ease,
      onUpdate: () => {
        $ship.css({
          left: `${state.x}px`,
          top: `${state.y}px`,
          transform: `translate(-50%, -50%) rotate(${state.rot}deg) scale(${state.sx}, ${state.sy})`,
        });
        this.shipRotation = state.rot;
        this.shipScaleX = state.sx;
        this.shipScaleY = state.sy;
      },
      onComplete,
    });
    this.angleTweens.push(tween);
    return tween;
  }

  // Map an SVG user-space point to pixels inside the host element. Robust to any
  // CSS scaling of the SVG. Returns null while the SVG isn't rendered yet.
  // (Uses raw SVG matrix APIs — jQuery has no equivalent for these.)
  svgToLocal(ux, uy, $anchor) {
    const svg = $anchor.get(0)?.ownerSVGElement;
    const host = this.$host.get(0);
    if (!svg || !host) return null;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;

    const pt = svg.createSVGPoint();
    pt.x = ux;
    pt.y = uy;
    const screen = pt.matrixTransform(ctm);
    const hostRect = host.getBoundingClientRect();
    return {x: screen.x - hostRect.left, y: screen.y - hostRect.top, scale: ctm.a};
  }

  // Move the planets' start/alignment positions (degrees) and rebuild.
  setStartAngles(earth, mars) {
    this.startAngles = {earth, mars};
    this.rebuild();
  }

  rebuild() {
    const wasPaused = this.timeline ? this.timeline.paused() : false;
    this.reset();
    this.init();
    if (wasPaused) this.timeline.pause();
  }

  // Update the alignment ranges at runtime. Each arg is [min, max] degrees.
  setAlignRanges(earth, mars) {
    this.alignRanges = {earth, mars};
    if (this.debug) this.drawDebug();
  }

  // Turn the reference markers on/off at runtime.
  setDebug(on) {
    this.debug = on;
    on ? this.drawDebug() : this.clearDebug();
  }

  // True when both planets are inside their alignment ranges. Ranges are
  // {earth: [min, max], mars: [min, max]} in degrees.
  isAligned(ranges = this.alignRanges) {
    return this.isWithinRange("earth", ranges.earth)
      && this.isWithinRange("mars", ranges.mars);
  }

  // True when a planet's current angle sits inside the [min, max] arc.
  isWithinRange(key, [min, max]) {
    return SolarSystem.angleInRange(this.angles[key] || 0, min, max);
  }

  // Wrap an angle into the 0 to 360 range.
  static normAngle(deg) {
    return ((deg % 360) + 360) % 360;
  }

  // The shortest turn between two angles, in degrees (-180 to 180).
  static shortestDelta(from, to) {
    return (((to - from + 180) % 360) + 360) % 360 - 180;
  }

  // Is the angle inside the range from min to max (going clockwise)? Handles
  // ranges that wrap past the top, like 350 to 10.
  static angleInRange(angle, min, max) {
    const norm = (deg) => ((deg % 360) + 360) % 360;
    const a = norm(angle), lo = norm(min), hi = norm(max);
    return lo <= hi ? (a >= lo && a <= hi) : (a >= lo || a <= hi);
  }

  // ---- Debug reference markers --------------------------------------------

  // Draws, per planet: a hollow ring at the initial (start-angle) position, an
  // arc spanning the alignment window, and a green/red dot at the window's
  // start/end. These are static guides on the orbit — the planet animates
  // through them, so you can see where your angles/ranges actually land.
  drawDebug() {
    const svg = $("#main-earth").get(0)?.ownerSVGElement;
    if (!svg) return;

    this.clearDebug();
    const layer = SolarSystem.svgEl("g", {id: "solar-debug", "pointer-events": "none"});

    for (const planet of ["earth", "mars"]) {
      const selector = `#main-${planet}`;
      const [min, max] = this.alignRanges[planet];

      // Alignment window arc + start (green) / end (red) dots.
      layer.appendChild(SolarSystem.svgEl("path", {
        d: this.orbitArc(selector, min, max),
        fill: "none", stroke: "#ffd54a", "stroke-width": 3, "stroke-opacity": 0.7,
      }));
      const start = this.pointAtAngle(selector, min);
      const end = this.pointAtAngle(selector, max);
      layer.appendChild(SolarSystem.svgEl("circle", {cx: start.x, cy: start.y, r: 7, fill: "#3ddc84"}));
      layer.appendChild(SolarSystem.svgEl("circle", {cx: end.x, cy: end.y, r: 7, fill: "#ff5252"}));

      // Initial position (hollow white ring).
      const init = this.pointAtAngle(selector, this.startAngles[planet]);
      layer.appendChild(SolarSystem.svgEl("circle", {
        cx: init.x, cy: init.y, r: 12, fill: "none", stroke: "#ffffff", "stroke-width": 2,
      }));

      // Destination (target-angle) position — where the planet animates TO.
      const dest = this.pointAtAngle(selector, this.targetAngles[planet]);
      layer.appendChild(SolarSystem.svgEl("circle", {
        cx: dest.x, cy: dest.y, r: 12, fill: "none", stroke: "#4ad9ff", "stroke-width": 3,
      }));
    }

    $(svg).append(layer);
  }

  clearDebug() {
    $("#solar-debug").remove();
  }

  // A planet's center and the sun's center, in SVG coordinates.
  planetGeometry(selector) {
    const $el = $(selector);
    const [sx, sy] = this.sunOrigin.split(/\s+/).map(Number);
    return {
      cx: parseFloat($el.attr("cx")),
      cy: parseFloat($el.attr("cy")),
      sx, sy,
    };
  }

  // The angle where a planet sits as drawn in the SVG (direction from sun to it).
  drawnAngle(key) {
    const {cx, cy, sx, sy} = this.planetGeometry(`#main-${key}`);
    return Math.atan2(cy - sy, cx - sx) * 180 / Math.PI;
  }

  // The point on a planet's orbit at angle A (0 = top, clockwise).
  pointAtAngle(selector, A) {
    const {cx, cy, sx, sy} = this.planetGeometry(selector);
    const r = Math.hypot(cx - sx, cy - sy);
    const phi = (A - 90) * Math.PI / 180; // turn our "0 = top" angle into the one the math uses
    return {x: sx + r * Math.cos(phi), y: sy + r * Math.sin(phi)};
  }

  // SVG path for the orbit arc swept clockwise from one angle to another.
  orbitArc(selector, fromDeg, toDeg) {
    const {cx, cy, sx, sy} = this.planetGeometry(selector);
    const r = Math.hypot(cx - sx, cy - sy);
    const p0 = this.pointAtAngle(selector, fromDeg);
    const p1 = this.pointAtAngle(selector, toDeg);
    const span = ((toDeg - fromDeg) % 360 + 360) % 360; // clockwise span
    const largeArc = span > 180 ? 1 : 0;
    return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${largeArc} 1 ${p1.x} ${p1.y}`;
  }

  // Build a namespaced SVG element (jQuery can't create SVG-namespaced nodes).
  static svgEl(tag, attrs) {
    const el = document.createElementNS("http://www.w3.org/2000/svg", tag);
    for (const key in attrs) el.setAttribute(key, attrs[key]);
    return el;
  }

  // ---- Playback -----------------------------------------------------------

  play() {
    this.timeline?.play();
  }

  pause() {
    this.timeline?.pause();
  }

  // Pause the orbit, then ease each planet the short way to its exact start
  // angle (the target's center) before settling. Used when the player locks in
  // an alignment. Calls onComplete once both planets have arrived.
  snapToStart(onComplete = null, duration = 0.45) {
    const deltas = {};
    for (const key of ["earth", "mars"]) {
      const current = this.angles[key] ?? this.startAngles[key];
      deltas[key] = SolarSystem.shortestDelta(current, this.startAngles[key]);
    }
    this.tweenPlanetsByDelta(deltas, {duration, ease: "power2.out", onComplete});
  }

  // Pause the orbit, then run the start→target transfer: each planet rotates by
  // its launch delta (see launchDeltas).
  // With tailSec the planets don't stop when they get there, they keep turning at
  // the same rate for that much longer. Nothing about the meeting point makes a
  // planet stop, so a trip that didn't work out should leave them still going round
  // rather than frozen on a rendezvous that never happened. They still pass through
  // their target angles at exactly `duration`, which is what the ship's timing is
  // measured against, so the tail changes nothing about the rendezvous.
  animateToTarget(onComplete = null, duration = 2, tailSec = 0) {
    this.positionGhosts();              // ensure ghosts sit at the start point
    this.$host.addClass("transferring"); // reveal them

    const deltas = this.launchDeltas();
    const stretch = duration > 0 ? (duration + tailSec) / duration : 1;
    for (const key in deltas) deltas[key] *= stretch;

    this.tweenPlanetsByDelta(deltas, {duration: duration + tailSec, ease: "linear", onComplete});
  }

  // How long after the meeting point Mars takes to clear the target circle: the
  // latest the capture window can close, and so how much longer than the transfer
  // the scene has to stay alive. From Mars's own speed, so it follows the schedule.
  marsDestinationExitSec(transferSec) {
    const p = this.marsEndPoint();
    const [sx, sy] = this.sunOrigin.split(/\s+/).map(Number);
    const orbitRadius = Math.hypot(p.x - sx, p.y - sy) || 1;
    const degPerSec = Math.abs(this.launchDeltas().mars) / (transferSec || 1);
    const speed = orbitRadius * degPerSec * Math.PI / 180; // SVG units per second
    return speed > 0 ? this.destinationRadiusSvg() / speed : 0;
  }

  // Back to the planets going round, after a press that came to nothing. The loop
  // starts its cycle on the moment they line up, which is exactly where a press
  // snapped them to, so it picks up there rather than at the point it was paused
  // at, which the planets have since moved away from.
  resumeOrbit() {
    this.killAngleTweens();
    this.timeline?.progress(0).play();
  }

  // Stop the planets dead where they are, for the entry burn. Sliding them on to
  // their target angles would drift Mars out from under the ship aimed at it.
  freezePlanets() {
    this.timeline?.pause();
    this.killAngleTweens();
  }

  // How far (signed degrees) each planet rotates during the transfer. Mars goes
  // the short way to its target angle; Earth covers the SAME arc scaled by the
  // orbital-speed ratio (turnsPerCycle.earth / .mars), since it sweeps that much
  // more angle in the same time. Earth's delta can exceed a half-turn, which is
  // why the transfer is driven by deltas rather than shortest-path-to-angle.
  launchDeltas() {
    const currentMars = this.angles.mars ?? this.startAngles.mars;
    const marsDelta = SolarSystem.shortestDelta(currentMars, this.targetAngles.mars);
    const speedRatio = this.turnsPerCycle.earth / this.turnsPerCycle.mars;
    return {mars: marsDelta, earth: marsDelta * speedRatio};
  }

  // Pause the loop and rotate each planet by the given signed degrees
  // ({earth, mars}). Shared by snapToStart / animateToTarget.
  tweenPlanetsByDelta(deltaByKey, {duration = 0.45, ease = "power2.out", onComplete = null} = {}) {
    this.timeline?.pause();
    this.killAngleTweens(); // cancel any in-flight snap/transfer first

    const planets = ["earth", "mars"];
    let done = 0;

    planets.forEach((key) => {
      const $anchor = $(`#main-${key}`);
      const $planet = this.$host.find(`.planet[data-planet="${key}"]`);
      const current = this.angles[key] ?? this.startAngles[key];

      const state = {a: current};
      const tween = gsap.to(state, {
        a: current + (deltaByKey[key] ?? 0),
        duration,
        ease,
        onUpdate: () => {
          this.angles[key] = state.a;
          this.positionPlanet(key, state.a, $anchor, $planet);
        },
        onComplete: () => {
          if (++done === planets.length && typeof onComplete === "function") onComplete();
        },
      });
      this.angleTweens.push(tween);
    });
  }

  // Cancel any in-flight snap / transfer tweens.
  killAngleTweens() {
    if (this.angleTweens) this.angleTweens.forEach((t) => t.kill());
    this.angleTweens = [];
  }

  toggle() {
    if (!this.timeline) return;
    this.timeline.paused() ? this.timeline.play() : this.timeline.pause();
  }

  reset() {
    this.killAngleTweens();
    this.shipTween = null;
    this.shipLocal = null;
    if (this.timeline) {
      this.timeline.kill();
      this.timeline = null;
    }
    this.clearDebug();
    this.clearTrajectories();
    this.aimGeo = null;
    this.$host.removeClass("transferring"); // hide the ghosts
  }

  // Tear down and rebuild a fresh orbiting system at the start positions. Use on
  // (re-)entering the game so a previous transfer/lock leaves no stale state.
  // cycleProgress (0 to 1) starts the loop part way through its cycle instead,
  // so the next alignment comes sooner (0.5 = Earth one lap in, Mars half).
  restart(cycleProgress = 0) {
    this.reset();
    this.init();
    if (cycleProgress > 0) this.timeline.progress(cycleProgress);
  }
}
